<template>
  <div>
    <v-dialog
      v-if="messageConfig && editMessage"
      v-model="isVisible"
      fullscreen
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="60"
          max-height="60"
        >
          <v-badge
            :value="editMessage.Important"
            color="red"
            overlap
            right
          >
            <template #badge>
              <v-icon

                dark
                small
              >
                fas fa-exclamation
              </v-icon>
            </template>

            <v-avatar
              :color="$t('Message.MessageSendDialog.Toolbar.Color')"
              size="42"
            >
              <v-icon color="indigo lighten-5">
                {{ $t('Message.MessageSendDialog.Toolbar.Icon') }}
              </v-icon>
            </v-avatar>
          </v-badge>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Message.MessageSendDialog.Toolbar.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Message.MessageSendDialog.Toolbar.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            class="ml-0"
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-2">
          <v-form
            ref="messageForm"
            v-model="messageFormValid"
            @submit.prevent
          >
            <v-container
              class="fill-height pa-0"
              fluid
            >
              <v-row dense>
                <v-col
                  cols="12"
                  sm="5"
                >
                  <v-select
                    v-model="editMessage.TempType"
                    :items="groupedTypes"
                    :label="$t('Message.MessageSendDialog.Field.MessageType.Label')"
                    :rules="editMessage.vuetifyFormFieldRules('Type')"
                    filled
                    hide-details="auto"
                    item-text="GroupName"
                    item-value="Id"
                    return-object
                  >
                    <template
                      #selection="data"
                    >
                      <div>
                        <v-avatar
                          :color="data.item.Color"
                          class="mr-1"
                          size="18"
                        >
                          <v-icon
                            :color="data.item.Color + ' lighten-5'"
                            size="10"
                          >
                            {{ data.item.Icon }}
                          </v-icon>
                        </v-avatar>
                        <span class="">{{ data.item.GroupName }} {{ data.item.Subject ? '- ' + data.item.Subject : '' }}</span>
                      </div>
                    </template>

                    <template
                      #item="data"
                    >
                      <v-list-item-avatar>
                        <v-avatar
                          :color="data.item.Color"
                          size="36"
                        >
                          <v-icon
                            :color="data.item.Color + ' lighten-5'"
                            small
                          >
                            {{ data.item.Icon }}
                          </v-icon>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ data.item.Subject }}</v-list-item-title>
                        <v-list-item-subtitle>{{ data.item.GroupName }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>

                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-text-field
                    v-model="editMessage.Type.Name"
                    :label="$t('Message.MessageSendDialog.Field.Sender.Label')"
                    :rules="editMessage.vuetifyFormFieldRules('Type.Name')"
                    filled
                    hide-details="auto"
                  />
                </v-col>

                <v-col
                  class="align-content-center"
                  cols="12"
                  sm="3"
                >
                  <v-checkbox
                    v-model="editMessage.Active"
                    :label="$t('Common.Button.MessageSend')"
                    class="pa-0 ma-0 mt-1"
                    color="success"
                    hide-details
                  />

                  <v-checkbox
                    v-model="editMessage.SendWithEmail"
                    :label="$t('Common.Button.EmailSend')"
                    class="pa-0 ma-0 mt-1"
                    color="blue"
                    hide-details
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="3"
                >
                  <v-menu
                    v-model="scheduledDateDialog"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    max-width="290px"
                    min-width="290px"
                    offset-y
                    transition="scale-transition"
                  >
                    <template #activator="{on}">
                      <v-text-field
                        v-model="computedDateFormattedMomentjs"
                        :label="$t('Message.MessageSendDialog.Field.DateSent.Label')"
                        :prepend-inner-icon="$t('Message.MessageSendDialog.Field.DateSent.Icon')"
                        clearable
                        dense
                        filled
                        hide-details
                        readonly
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="scheduledDate"
                      :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                      :min="new Date().toISOString().substring(0, 10)"
                      @input="scheduledDateDialog = false"
                    />
                  </v-menu>
                </v-col>

                <v-col
                  cols="12"
                  sm="2"
                >
                  <v-menu
                    ref="scheduledTimeDialog"
                    v-model="scheduledTimeDialog"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="scheduledTime"
                    max-width="290px"
                    min-width="290px"
                    offset-y
                    transition="scale-transition"
                  >
                    <template #activator="{on}">
                      <v-text-field
                        v-model="scheduledTime"
                        :label="$t('Message.MessageSendDialog.Field.HourSent.Label')"
                        :prepend-inner-icon="$t('Message.MessageSendDialog.Field.HourSent.Icon')"
                        clearable
                        dense
                        filled
                        hide-details
                        readonly
                        v-on="on"
                      />
                    </template>

                    <v-time-picker
                      v-if="scheduledTimeDialog"
                      v-model="scheduledTime"
                      :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                      :min="scheduledDate === new Date().toISOString().substring(0, 10) ? new Date().toISOString().substring(11, 16) : undefined"
                      format="24hr"
                      @click:minute="$refs.scheduledTimeDialog.save(scheduledTime)"
                    />
                  </v-menu>
                </v-col>

                <v-col
                  cols="12"
                  sm="4"
                >
                  <v-menu
                    v-model="expiredAtDialog"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    max-width="290px"
                    min-width="290px"
                    offset-y
                    transition="scale-transition"
                  >
                    <template #activator="{on}">
                      <v-text-field
                        v-model="computedExpiredAtFormattedMomentjs"
                        :label="$t('Message.MessageSendDialog.Field.ExpiredAt.Label')"
                        :prepend-inner-icon="$t('Message.MessageSendDialog.Field.ExpiredAt.Icon')"
                        clearable
                        dense
                        filled
                        hide-details
                        readonly
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="expiredAt"
                      :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                      :min="new Date().toISOString().substring(0, 10)"
                      @input="expiredAtDialog = false"
                    />
                  </v-menu>
                </v-col>

                <v-col
                  class="align-content-center"
                  cols="12"
                  sm="3"
                >
                  <v-checkbox
                    v-model="editMessage.Important"
                    :label="$t('Message.MessageSendDialog.Field.Important.Label')"
                    :off-icon="$t('Message.MessageSendDialog.Field.Important.IconOff')"
                    :on-icon="$t('Message.MessageSendDialog.Field.Important.IconOn')"
                    class="pa-0 ma-0"
                    color="red"
                    dense
                    hide-details
                  />
                </v-col>

                <v-col cols="12">
                  <v-autocomplete
                    v-model="editMessage.To"
                    :items="messageConfig.Apps.all()"
                    :label="$t('Message.MessageSendDialog.Field.SentTo.Label')"
                    :placeholder="$t('Message.MessageSendDialog.Field.SentTo.Placeholder')"
                    chips
                    clearable
                    dense
                    filled
                    hide-details
                    item-text="Name"
                    item-value="Key"
                    multiple
                  >
                    <template
                      #selection="data"
                    >
                      <v-chip
                        :value="data.selected"
                        class="chip--select-multi"
                        close
                        @input="removeRecipient(data.item)"
                        @click:close="removeRecipient(data.item)"
                      >
                        <v-avatar
                          color="blue-grey"
                          dark
                        >
                          <span
                            v-if="data.item.Name"
                            class="white--text text-h5"
                          >{{ data.item.Name.charAt(0).toLocaleUpperCase($i18n.locale) }}</span>
                        </v-avatar>
                        {{ data.item.Name }}
                      </v-chip>
                    </template>
                    <template
                      #item="data"
                    >
                      <v-list-item-avatar>
                        <v-avatar
                          color="blue-grey"
                          dark
                          size="32"
                        >
                          <span
                            v-if="data.item.Name"
                            class="white--text text-h5"
                          >{{ data.item.Name.charAt(0).toLocaleUpperCase($i18n.locale) }}</span>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="editMessage.Type.Subject"
                    :label="$t('Message.MessageSendDialog.Field.MessageTitle.Label')"
                    :placeholder="$t('Message.MessageSendDialog.Field.MessageTitle.Placeholder')"
                    :rules="editMessage.vuetifyFormFieldRules('Type.Subject')"
                    dense
                    filled
                    hide-details="auto"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="isVisible"
                dense
              >
                <v-col cols="12">
                  <unlayer-editor
                    ref="emailEditor"
                    :appearance="emailEditor.appearance"
                    :editor-id="emailEditor.id"
                    :min-height="emailEditor.minHeight"
                    :options="emailEditor.options"
                    :project-id="emailEditor.projectId"
                    :tools="emailEditor.tools"
                    @load="emailEditorLoaded"
                    @ready="emailEditorReady"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-2">
          <v-btn
            :disabled="isSavingTemplate"
            :loading="isSavingTemplate"
            class="light-blue--text text--darken-1"
            outlined
            small
            @click="onSaveTemplateClick"
          >
            Save Template
          </v-btn>

          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            small
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            class="elevation-0"
            color="light-green"
            dark
            small
            @click="onSendButtonClick"
          >
            {{ $t('Common.Button.Send').toLocaleUpperCase($i18n.locale) }}
            <v-icon right>
              send
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      v-if="editMessage"
      :html-content="editMessage?.To?.length > 0 ? $t('Message.ConfirmSendDialog.ContentSpecific', {domains: getToDomains()}) : $t('Message.ConfirmSendDialog.ContentAll')"
      :html-title="$t('Message.ConfirmSendDialog.Title')"
      :visible.sync="confirmDialogSendVisible"
      :width="560"
      no-button-class="v-btn--outline primary--text"
      title-class="primary white--text lighten-0"
      yes-button-class="primary white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmSendMessage"
    />
  </div>
</template>

<script>
import moment from 'moment'
import MessageModel from '@/api/Models/messages/MessageModel'
import UnlayerEditor from '@/components/common/unlayer-editor/UnlayerEditor.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'

export default {
  name      : 'MessageSendDialog',
  components: {
    ConfirmDialog,
    UnlayerEditor
  },
  mixins: [],
  props : {
    visible: {
      type    : Boolean,
      default : false,
      required: true
    },

    message: {
      type   : Object,
      default: undefined
    },

    messageConfig: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {

      confirmDialogSendVisible: false,
      isSavingTemplate        : false,
      scheduledDateDialog     : false,
      scheduledTimeDialog     : false,
      expiredAtDialog         : false,
      expireInNextDays        : 180,
      expiredAt               : '',
      scheduledDate           : '',
      scheduledTime           : '',
      messageFormValid        : false,
      editMessage             : new MessageModel(),
      emailEditor             : {
        id       : 'email-editor-id',
        // projectId: 1,
        locale   : 'en-US',
        minHeight: '830px',
        tools    : {},
        options  : {
          version    : 'stable', // Specify 'latest', 'stable' or a specific version number
          displayMode: 'email', // email, web, popup
          features   : {
            textEditor: {
              tables: true, // Enable tables in the text editor
              emojis: true // Enable emoji support in the text editor
            }
          },
          mergeTags: {
            user: {
              name     : 'User',
              mergeTags: {
                first_name: {
                  name  : 'First Name',
                  value : '{{first_name}}',
                  sample: 'John'
                },
                last_name: {
                  name  : 'Last Name',
                  value : '{{last_name}}',
                  sample: 'Doe'
                }
              }
            },
            shop: {
              name     : 'Shop',
              mergeTags: {
                domain_name: {
                  name  : 'Domain Name',
                  value : '{{domain_name}}',
                  sample: 'xxxxx.on.getfood.gr'
                },
                company_name: {
                  name  : 'Company Name',
                  value : '{{company_name}}',
                  sample: 'My Shop'
                }
              }
            }
          }
        }
      }
    }
  },
  computed: {
    groupedTypes() {
      return this.messageConfig.Types.groupedTypesForSelect()
    },
    computedExpiredAtFormattedMomentjs: {
      get() {
        return this.expiredAt ? moment(this.expiredAt).format('DD/MM/YYYY') : ''
      },
      set(val) {
        this.expiredAt = val || ''
      }
    },
    computedDateFormattedMomentjs: {
      get() {
        return this.scheduledDate ? moment(this.scheduledDate).format('DD/MM/YYYY') : ''
      },
      set(val) {
        this.scheduledDate = val || ''
      }
    },
    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.scheduledDateDialog = false
        this.scheduledTimeDialog = false
        this.expiredAtDialog = false
        this.scheduledDate = ''
        this.scheduledTime = ''
        this.expiredAt = moment().add(this.expireInNextDays, 'days').toISOString().substring(0, 10)
        this.messageFormValid = false

        if (this.message) {
          this.editMessage = new MessageModel(this.message)
        } else {
          this.editMessage = new MessageModel()
        }
        this.$bus.$on(window.SocketCommand.Message.SaveTemplate, this.onSaveTemplateResult)
      } else {
        this.editMessage = null
        this.$bus.$off(window.SocketCommand.Message.SaveTemplate, this.onSaveTemplateResult)
      }
    }
  },
  created() {
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    htmlDecode(input) {
      const doc = new DOMParser().parseFromString(input, 'text/html')
      return doc.documentElement.innerHTML
    },

    getToDomains() {
      const selectedApps = this.editMessage?.To || []
      const allApps = this.messageConfig.Apps.all() || []
      return selectedApps.map(selectedAppKey => allApps.find(app => app.Key === selectedAppKey)?.Name).join(', ')
    },

    onSaveTemplateClick() {
      this.isSavingTemplate = true
      const payload = this.editMessage.clone()
      this.$refs.emailEditor.editor.exportHtml(
        (data) => {
          payload.Source = data?.design || null
          payload.Body = this.htmlDecode(data?.html || '')

          this.$refs.emailEditor.editor.exportPlainText(
            (data) => {
              payload.BodyText = data?.text || ''

              window.callAS(window.SocketCommand.Message.SaveTemplate, {
                Id      : payload?.Id || null,
                Type    : payload?.Type || null,
                Body    : payload?.Body || '',
                BodyText: payload?.BodyText || '',
                Source  : payload?.Source || ''
              })
            }
          )
        },
        {
          minify      : true,
          inlineStyles: true
        }
      )
    },
    onSaveTemplateResult(data, payload) {
      this.isSavingTemplate = false

      if (data && data.status === 'success') {
        if (data?.Id) this.editMessage.Id = data.Id
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Message.Template.SaveSuccess'),
          type: 'success',
          icon: 'info'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onSendButtonClick() {
      if (this.$refs.messageForm.validate()) {
        this.confirmDialogSendVisible = true
      }
    },
    onConfirmSendMessage() {
      const d = new Date()
      const dateNowStr = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
      const timeNowStr = `${d.getHours()}:${d.getMinutes()}`
      const payload = this.editMessage.clone()

      this.$refs.emailEditor.editor.exportHtml(
        (data) => {
          payload.Source = data?.design || null
          payload.Body = this.htmlDecode(data?.html || '')

          this.$refs.emailEditor.editor.exportPlainText(
            (data) => {
              payload.BodyText = data?.text || ''

              if (this.expiredAt) {
                payload.ExpiredAt = `${this.expiredAt} 23:59:59`
              }

              if (this.scheduledDate) {
                payload.Scheduled = `${this.scheduledDate} ${this.scheduledTime || timeNowStr}:00`
              }

              if (this.scheduledTime) {
                payload.Scheduled = `${this.scheduledDate || dateNowStr} ${this.scheduledTime}:00`
              }

              this.editMessage.save({Message: payload})
              this.onCancelButtonClick()
            }
          )
        },
        {
          minify      : true,
          inlineStyles: true
        }
      )
    },

    onCancelButtonClick() {
      if (this.$refs.messageForm) this.$refs.messageForm.resetValidation()
      this.editMessage = new MessageModel()
      this.scheduledDate = ''
      this.scheduledTime = ''
      this.isVisible = false
    },

    removeRecipient(item) {
      const index = this.editMessage.To.indexOf(item.Key)
      if (index >= 0) this.editMessage.To.splice(index, 1)
    },

    // called when the editor is created
    emailEditorLoaded() {
      if (this.editMessage?.Source) {
        this.$refs.emailEditor.editor.loadDesign(this.editMessage.Source)
      }
    },

    // called when the editor has finished loading
    emailEditorReady() {
    },
    emailEditorSaveDesign() {
      this.$refs.emailEditor.editor.saveDesign((design) => {

        console.log('>>>>>>>> emailEditorSaveDesign', design)
      })
    },
    emailEditorExportHtml() {
      this.$refs.emailEditor.editor.exportHtml((data) => {

        console.log('>>>>>>>> emailEditorExportHtml', data)
      })
    },
    emailEditorExportPlainText() {
      this.$refs.emailEditor.editor.exportPlainText((data) => {

        console.log('>>>>>>>> emailEditorExportPdf', data.text)
      })
    },
    emailEditorExportImage() {
      this.$refs.emailEditor.editor.exportImage((data) => {

        console.log('>>>>>>>> emailEditorExportPdf', data.url)
      })
    },
    emailEditorExportPdf() {
      this.$refs.emailEditor.editor.exportPdf((data) => {

        console.log('>>>>>>>> emailEditorExportPdf', data.url)
      })
    },
    emailEditorExportZip() {
      this.$refs.emailEditor.editor.exportZip((data) => {

        console.log('>>>>>>>> emailEditorExportZip', data.url)
      })
    }

  }
}
</script>

<style scoped>

</style>
